import { logger } from 'utils/logging'

/**
 * Tracks blob Object URLs created by this module
 * so they can be revoked when replaced, preventing memory leaks.
 */
const activeBlobUrls = new Set<string>()

/** Create a local image URL tracked by the shared history/unmount cleanup. */
export function createImageBlobUrl(blob: Blob): string {
  const url = URL.createObjectURL(blob)
  activeBlobUrls.add(url)
  return url
}

/**
 * Checks whether a URL is a blob: or data: URL (already same-origin).
 */
export function isLocalUrl(url: string): boolean {
  if (!url) return false
  return url.startsWith('blob:') || url.startsWith('data:')
}

/**
 * Extract hostname and pathname from a URL for safe logging
 * (strips query params that may contain signed tokens).
 */
function safeLogUrl(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.hostname}${parsed.pathname}`
  } catch {
    return '(invalid URL)'
  }
}

/**
 * Validates that a URL is safe to fetch (HTTPS only).
 */
function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Converts a remote signed URL to a same-origin blob Object URL.
 *
 * Required for Mixpanel session recording compatibility:
 * signed GCS URLs are cross-origin and expire, so Mixpanel cannot
 * capture the pixel data during replay.
 * Blob Object URLs (blob:https://app-domain/...) are same-origin
 * and can be inlined by the session recorder.
 *
 * If the URL is already a blob: or data: URL, returns it unchanged.
 * On failure, returns the original URL as a fallback.
 */
export async function signedUrlToBlobUrl(
  url: string,
  logContext?: { component: string; action: string }
): Promise<string> {
  if (!url || isLocalUrl(url)) {
    return url
  }

  if (!isAllowedUrl(url)) {
    logger.error('Blocked non-HTTPS URL in signedUrlToBlobUrl', null, {
      context: {
        component: logContext?.component ?? 'signedUrlToBlobUrl',
        action: logContext?.action ?? 'convert',
        metadata: { url: safeLogUrl(url) }
      }
    })
    return url
  }

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} fetching image`)
    }
    const blob = await response.blob()
    return createImageBlobUrl(blob)
  } catch (error) {
    logger.error('Failed to convert signed URL to blob URL', error, {
      context: {
        component: logContext?.component ?? 'signedUrlToBlobUrl',
        action: logContext?.action ?? 'convert',
        metadata: { url: safeLogUrl(url) }
      }
    })
    // Fallback: return original signed URL so the image still displays
    return url
  }
}

/**
 * Revoke a blob Object URL if it was created by this module.
 * Safe to call with any URL — non-blob or untracked URLs are silently ignored.
 */
export function revokeBlobUrl(url: string): void {
  if (url.startsWith('blob:') && activeBlobUrls.has(url)) {
    URL.revokeObjectURL(url)
    activeBlobUrls.delete(url)
  }
}

/**
 * Revoke all tracked blob Object URLs (cleanup on unmount).
 */
export function revokeAllBlobUrls(): void {
  activeBlobUrls.forEach(url => URL.revokeObjectURL(url))
  activeBlobUrls.clear()
}
